import React, { useMemo } from "react";
import {
  TextField,
  MenuItem,
  Select,
  Button,
  FormHelperText,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import EditText from "../../components/editText";
import { useFormik } from "formik";
import * as Yup from "yup";
import { NOTIFICATION_TEMPLATE_TYPE } from "../../constants/enums";
import {
  useAddEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useGetEmailTemplateByIdQuery,
} from "../../services/emailTemplate";
import { showToast } from "../../helpers";

const AddEmailTemplate = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEdit = Boolean(id);

  const { data: detailData, isFetching } = useGetEmailTemplateByIdQuery(
    { id: id as string },
    { skip: !isEdit }
  );

  const [addEmailTemplate, { isLoading: isAdding }] =
    useAddEmailTemplateMutation();
  const [updateEmailTemplate, { isLoading: isUpdating }] =
    useUpdateEmailTemplateMutation();

  const templateTypeOptions = useMemo(() => {
    return Object.keys(NOTIFICATION_TEMPLATE_TYPE)
      .filter((k) => isNaN(Number(k)))
      .map((name) => ({
        label: name,
        value: (NOTIFICATION_TEMPLATE_TYPE as any)[name] as number,
      }));
  }, []);

  const stripHtml = (html: string) =>
    (html || "")
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Template name is required"),
    templateType: Yup.number().required("Template type is required"),
    title: Yup.string().trim().required("Title is required"),
    content: Yup.string().test(
      "content-not-empty",
      "Content is required",
      (val) => stripHtml(val || "").length > 0
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: detailData?.data?.name || "",
      templateType: detailData?.data?.templateType || "",
      title: detailData?.data?.title || "",
      content: detailData?.data?.content || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const body: any = {
          name: values.name.trim(),
          type: 1, // static as requested for email 1 is for sms
          templateType: Number(values.templateType),
          title: values.title.trim(),
          content: values.content,
        };

        if (isEdit) {
          await updateEmailTemplate({ body, id: id as string }).unwrap();
        } else {
          await addEmailTemplate(body).unwrap();
        }
        showToast(
          `${
            isEdit
              ? "Email template updated successfully"
              : "Email template saved successfully"
          }`
        );
        navigate(-1);
      } catch (e) {
        // TODO: integrate proper error handling/toast if available
        console.error(e);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      <div className="main_title">
        <h1>{isEdit ? "Edit Email Template" : "Add Email Template"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate(-1)}>Email Template</button> -
          {isEdit ? "Edit Email Template" : "Add Email Template"}
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label htmlFor="name">Template Name</label>
              <TextField
                id="name"
                name="name"
                placeholder="Enter Template Name"
                fullWidth
                hiddenLabel
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={
                  formik.touched.name &&
                  formik.errors.name &&
                  String(formik.errors.name)
                }
              />
            </div>
            <div className="control_group w_50">
              <label htmlFor="templateType">Template Type</label>
              <Select
                id="templateType"
                name="templateType"
                fullWidth
                displayEmpty
                value={
                  formik.values.templateType === ""
                    ? ""
                    : Number(formik.values.templateType)
                }
                onChange={(e) =>
                  formik.setFieldValue("templateType", e.target.value)
                }
                onBlur={() => formik.setFieldTouched("templateType", true)}
                disabled={isFetching}
              >
                <MenuItem disabled value="">
                  Select Template Type
                </MenuItem>
                {templateTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.templateType && formik.errors.templateType && (
                <FormHelperText error>
                  {formik.errors.templateType as string}
                </FormHelperText>
              )}
            </div>
            <div className="control_group w_100">
              <label htmlFor="title">Title</label>
              <TextField
                id="title"
                name="title"
                placeholder="Enter Title"
                fullWidth
                hiddenLabel
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={
                  formik.touched.title &&
                  formik.errors.title &&
                  String(formik.errors.title)
                }
              />
            </div>
            <div className="control_group w_100">
              <label htmlFor="content">Message</label>
              {isEdit && !formik.values.content ? null : (
                <EditText
                  content={formik.values.content}
                  setContent={(val: string) =>
                    formik.setFieldValue("content", val)
                  }
                />
              )}
              {formik.touched.content && formik.errors.content && (
                <FormHelperText error>
                  {formik.errors.content as string}
                </FormHelperText>
              )}
            </div>
          </div>
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={formik.isSubmitting || isAdding || isUpdating}
            >
              {isEdit ? "Update" : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddEmailTemplate;
